import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarPlus, Loader2, LogOut, MapPin, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const EVENT_TYPES = ["Conference", "Trade Fair", "Exhibition", "Summit", "B2B Meeting", "Business Forum", "Workshop", "Seminar"];
const STATUS_STYLE = {
  LIVE: "text-emerald-400 border-emerald-500/40",
  PENDING_APPROVAL: "text-amber-400 border-amber-500/40",
  REJECTED: "text-red-400 border-red-500/40",
};
const EMPTY_FORM = { name: "", type: "Conference", industry: "", city: "", country: "", venue: "", start_date: "", end_date: "", expected_attendees: 0, exhibitors: 0, description: "", website: "", tags: "" };

export default function OrganiserDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [working, setWorking] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = useCallback(() => {
    api.get("/organiser/me").then((r) => setData(r.data)).catch((err) => {
      if (err.response?.status === 401 || err.response?.status === 403) navigate("/events/organiser/login");
    });
  }, [navigate]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setWorking(true);
    try {
      const payload = { ...form, expected_attendees: Number(form.expected_attendees) || 0, exhibitors: Number(form.exhibitors) || 0,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [] };
      const { data: res } = await api.post("/organiser/events", payload);
      toast.success(res.message);
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setWorking(false); }
  };

  const logout = async () => {
    await api.post("/organiser/logout").catch(() => {});
    navigate("/events/organiser/login");
  };

  if (!data) return <div className="max-w-5xl mx-auto px-8 py-24 text-sm font-mono text-slate-500 animate-pulse" data-testid="organiser-loading">Loading organiser portal…</div>;
  const { organiser, events, stats } = data;

  return (
    <section className="section-dark min-h-screen py-12 px-4 md:px-8" data-testid="organiser-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="gold-kicker mb-1">Organiser Portal</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-100" data-testid="organiser-name">{organiser.org_name}</h1>
            <p className="text-xs font-mono text-slate-500 mt-1">{organiser.organiser_id} · {organiser.email}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowForm(!showForm)} data-testid="organiser-add-event-toggle" className="btn-gold text-xs">
              <CalendarPlus size={13} /> ADD UPCOMING EVENT
            </button>
            <button onClick={logout} data-testid="organiser-logout-btn" className="btn-outline text-xs"><LogOut size={13} /></button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="organiser-stats">
          {[["Submitted", stats.submitted], ["Live", stats.live], ["Pending Review", stats.pending], ["Rejected", stats.rejected]].map(([l, v]) => (
            <div key={l} className="card-surface p-4 text-center" data-testid={`organiser-stat-${l.toLowerCase().replace(/ /g, "-")}`}>
              <p className="font-mono text-2xl font-bold text-gold">{v}</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">{l}</p>
            </div>
          ))}
        </div>

        {showForm && (
          <form onSubmit={submit} className="card-surface hairline-gold p-6 md:p-8 mb-8 space-y-4" data-testid="organiser-event-form">
            <h2 className="font-display text-lg font-bold text-slate-100">SUBMIT AN UPCOMING EVENT</h2>
            <p className="text-xs text-slate-500 -mt-2">Events go live after admin approval.</p>
            <input value={form.name} onChange={set("name")} placeholder="Event name" required data-testid="event-name-input" className="input-dark text-sm w-full" />
            <div className="grid sm:grid-cols-3 gap-4">
              <select value={form.type} onChange={set("type")} data-testid="event-type-select" className="input-dark text-sm">
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input value={form.industry} onChange={set("industry")} placeholder="Industry (e.g. Manufacturing)" required data-testid="event-industry-input" className="input-dark text-sm" />
              <input value={form.venue} onChange={set("venue")} placeholder="Venue" data-testid="event-venue-input" className="input-dark text-sm" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={form.city} onChange={set("city")} placeholder="City" required data-testid="event-city-input" className="input-dark text-sm" />
              <input value={form.country} onChange={set("country")} placeholder="Country" required data-testid="event-country-input" className="input-dark text-sm" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1.5">Start date</label>
                <input type="date" value={form.start_date} onChange={set("start_date")} required data-testid="event-start-date" className="input-dark text-sm w-full" /></div>
              <div><label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1.5">End date</label>
                <input type="date" value={form.end_date} onChange={set("end_date")} data-testid="event-end-date" className="input-dark text-sm w-full" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input type="number" min="0" value={form.expected_attendees} onChange={set("expected_attendees")} placeholder="Expected attendees" data-testid="event-attendees-input" className="input-dark text-sm" />
              <input type="number" min="0" value={form.exhibitors} onChange={set("exhibitors")} placeholder="Expected exhibitors" data-testid="event-exhibitors-input" className="input-dark text-sm" />
            </div>
            <input value={form.tags} onChange={set("tags")} placeholder="Tags (comma separated)" data-testid="event-tags-input" className="input-dark text-sm w-full" />
            <input value={form.website} onChange={set("website")} placeholder="Event website (optional)" data-testid="event-website-input" className="input-dark text-sm w-full" />
            <textarea value={form.description} onChange={set("description")} placeholder="Event description" rows={3} data-testid="event-description-input" className="input-dark text-sm w-full" />
            <button type="submit" disabled={working} data-testid="organiser-submit-event-btn" className="btn-gold text-xs !px-7 !py-3.5">
              {working ? <Loader2 size={13} className="animate-spin" /> : "SUBMIT FOR APPROVAL"}
            </button>
          </form>
        )}

        <div className="card-surface" data-testid="organiser-events-list">
          <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
            <h2 className="font-display text-base font-bold text-slate-100">MY EVENTS</h2>
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{events.length} total</span>
          </div>
          <div className="divide-y divide-slate-800">
            {events.map((e) => (
              <div key={e.id} className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-4" data-testid={`organiser-event-${e.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-100">{e.name}</p>
                  <p className="text-[10px] font-mono text-slate-500 mt-0.5 flex items-center gap-2">
                    <span className="flex items-center gap-1"><CalendarDays size={10} />{e.dates}</span>
                    <span className="flex items-center gap-1"><MapPin size={10} />{e.city}, {e.country}</span>
                  </p>
                </div>
                <span className={`text-[10px] font-mono uppercase tracking-widest border px-2.5 py-1 rounded-sm ${STATUS_STYLE[e.status] || "text-slate-400 border-slate-600"}`} data-testid={`organiser-event-status-${e.id}`}>{e.status.replace(/_/g, " ")}</span>
                {e.status === "LIVE" && <Link to="/events" className="text-[10px] font-mono text-gold hover:text-gold-light uppercase tracking-widest">View public →</Link>}
              </div>
            ))}
            {events.length === 0 && (
              <p className="px-5 py-10 text-center text-sm text-slate-500" data-testid="organiser-events-empty">No events yet — add your first upcoming event above.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
